import type React from 'react';
import { graphql } from 'react-apollo';

import type { CmlContent } from '__generated__/graphql-types-do-not-use';
import { branch, compose, withProps } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import requestCountry from 'js/lib/requestCountry';

import Naptime from 'bundles/naptimejs';
import S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import { GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS } from 'bundles/unified-description-page-common/constants';
import {
  aboutCmlDescriptionOverrides,
  aboutDescriptionOverrides,
} from 'bundles/unified-description-page-common/data/google-gateways-india-localization/aboutDescriptionOverrides';
import faqOverrides from 'bundles/unified-description-page-common/data/google-gateways-india-localization/faqOverrides';
import SpecializationIdQuery from 'bundles/unified-description-page-common/queries/SpecializationIdFromSlugQuery.graphql';
import type {
  SpecializationIdFromSlugQuery,
  SpecializationIdFromSlugQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/SpecializationIdFromSlugQuery';
import type { FaqFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';

export type PropsToComponent = {
  overrides?: {
    description: string;
    cmlDescription: CmlContent;
    faqs: Array<FaqFragment>;
  };
  children: React.ReactNode;
};

const withGoogleIndiaOverrides = compose<PropsToComponent, {}>(
  connectToRouter(({ params }) => {
    return {
      slug: params.productSlug,
    };
  }),
  withProps(({ slug }: { slug: string }) => {
    const slugHasOverrides = Object.values(GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS).some(
      (overrideSlug) => slug === overrideSlug
    );

    return {
      enableContentOverrides: !!slugHasOverrides && requestCountry.get() === 'IN',
    };
  }),
  graphql<
    { slug: string; enableContentOverrides: boolean },
    SpecializationIdFromSlugQuery,
    SpecializationIdFromSlugQueryVariables,
    { s12nId?: string }
  >(SpecializationIdQuery, {
    skip: ({ enableContentOverrides }: { enableContentOverrides: boolean }) => !enableContentOverrides,
    props: ({ data }) => {
      return {
        s12nId: data?.Specialization?.queryBySlug?.id,
      };
    },
    options: {
      context: { clientName: 'gatewayGql' },
    },
  }),
  branch(
    ({ s12nId, enableContentOverrides }: { s12nId: string; enableContentOverrides: boolean }) => {
      // Ensure s12nId is defined as well or else withS12nSubscriptionPrice will infinitely prevent render.
      return !!s12nId && enableContentOverrides;
    },
    compose(
      Naptime.createContainer(({ s12nId }: { s12nId: string }) => {
        return {
          s12nDerivatives: S12nDerivativesV1.get(s12nId, {
            fields: ['catalogPrice'],
            required: false,
          }),
        };
      }),
      withProps(({ s12nDerivatives, slug }: { s12nDerivatives: S12nDerivativesV1; slug: string }) => {
        const s12nSubscriptionPrice = s12nDerivatives && s12nDerivatives.catalogPrice!;

        return {
          overrides: {
            // @ts-expect-error TS2722 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            description: aboutDescriptionOverrides[slug](s12nSubscriptionPrice?.amount),
            // @ts-expect-error TS2722 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            cmlDescription: aboutCmlDescriptionOverrides[slug](s12nSubscriptionPrice?.amount),
            // @ts-expect-error TS2722 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            faqs: faqOverrides[slug](s12nSubscriptionPrice?.amount),
          },
        };
      })
    )
  )
);

export default withGoogleIndiaOverrides;
